<template>
  <section class="bg-black4 font-Spectral">
    <AtomsHeading3 class="mb-6 text-white md:mb-2" lang="en">Recommended Articles</AtomsHeading3>
    <div class="article-area">
      <div v-for="enStory in enStoryLists" :key="enStory.title" class="article-area__article group">
        <nuxt-link :to="`/stories/${enStory.id}/`">
          <div class="eye-catch before:duration-500 group-hover:before:bg-white-transparent-15">
            <OptimizedImage :alt="`${enStory.title}の画像`" :image-url="enStory.thumbnailImage.url" :optimized-image-options="{ width: 560 }" />
            <div class="eye-catch__bottom-grad"></div>
          </div>
          <div class="content">
            <div class="content__providerName">
              <p class="text-gray1 group-hover:!text-gold2 md:text-sm md:tracking-[1px] md:text-white">{{ enStory.provider.name }}</p>
            </div>
            <div class="content__title">
              <h3 class="text-white group-hover:!text-gold2 md:text-sm md:tracking-[0.04em]">{{ enStory.title }}</h3>
            </div>
          </div>
          <div class="date">
            <p class="text-gray1 md:text-gray2">{{ enStory.date }}</p>
          </div>
        </nuxt-link>
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import { useIsSP } from '@/composables/ui/useIsSP';
import type { MinimalEnStoryListViewModel } from '@/models/wabunka/enStories';
import { OptimizedImage } from '@/wabunka-src/common/components';

interface State {
  isSP: Ref<boolean>;
}

interface Props {
  enStories: MinimalEnStoryListViewModel;
}

const props = defineProps<Props>();

const state = reactive<State>({
  isSP: useIsSP(),
});
const enStoryLists = computed(() => {
  return props.enStories.slice(0, state.isSP ? 2 : props.enStories.length);
});
</script>

<style lang="scss" scoped>
a {
  text-decoration: none;
}

.article-area {
  margin: 0 -16px 0 0;
  overflow-x: scroll;
  display: flex;
  -ms-overflow-style: none; /* IE, Edge 対応 */
  scrollbar-width: none; /* Firefox 対応 */
  justify-content: space-between;

  @apply md:m-0 md:flex-nowrap md:justify-normal;

  &::-webkit-scrollbar {
    /* Chrome, Safari 対応 */
    display: none;
  }
  &__ {
    &article {
      overflow: hidden;
      transition: 0.5s;
      width: 49%;
      margin: 0 16px 0 0;

      @apply md:relative md:min-w-[286px] md:max-w-[283px] md:pt-[20px];

      &:hover {
        .eye-catch {
          :deep(img) {
            -webkit-transform: scale(1.1);
            transform: scale(1.1);
            transition: 0.5s;
          }
        }
      }
      .eye-catch {
        overflow: hidden;
        position: relative;
        &::before {
          content: '';
          position: absolute;
          top: 0;
          bottom: 0;
          width: 100%;
          display: block;
          z-index: 2;
        }
        &__bottom-grad {
          position: absolute;
          width: 100%;
          height: 125px;
          bottom: 0;
          left: 0;
          background: linear-gradient(to bottom, rgba(51, 51, 51, 0) 0%, rgba(51, 51, 51, 0.5) 100%);
        }
        :deep(img) {
          transition: 0.5s;
          object-fit: cover;
        }
      }

      .content {
        margin: 16px 0 8px;

        @apply m-4 md:absolute md:bottom-0;

        &__ {
          &providerName {
            margin-bottom: 4px;
            p {
              font-size: 11px;
              line-height: 22px;
              letter-spacing: 0.5px;
              transition: all 0.5s;
            }
          }
          &title {
            h3 {
              font-size: 12px;
              line-height: 22px;
              letter-spacing: 0.5px;
              transition: all 0.5s;

              @apply md:text-sm md:tracking-[0.04em];
            }
          }
        }
      }
      .date {
        margin: 0 0 16px;

        @apply md:absolute md:right-0 md:top-0 md:m-0 md:block;

        p {
          font-family: Playfair Display;
          font-size: 12px;
          line-height: 12px;
          letter-spacing: 1px;
        }
      }
    }
  }
}
</style>
